import { toCsv } from '@helpers/csv.helper';
import AdminDownloadSvg from '@assets/svg/AdminDownload';
import { Button } from '@my-medication/shared-components/ui/Button';
import { type FunctionComponent, useImperativeHandle, useState } from 'react';

import { type DownloadCsvProps } from './DownloadCsv.types';

const DownloadCsv: FunctionComponent<DownloadCsvProps> = ({
  ref,
  data,
  fetchData,
  columns,
  fileName = 'csv',
  label = 'Download CSV',
  loadingLabel,
  children,
  dataTestid,
  className,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const download = async () => {
    let rows = data ?? [];

    if (fetchData) {
      setIsLoading(true);
      try {
        rows = await fetchData();
      } finally {
        setIsLoading(false);
      }
    }

    const csv = toCsv(rows, columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  useImperativeHandle(ref, () => ({ download }), [data, fetchData, columns, fileName]);

  return (
    <div className={className} data-testid={dataTestid}>
      {children ? (
        children({ download, isLoading })
      ) : (
        <Button
          name="download-csv"
          variant="ghost"
          onClick={download}
          disabled={isLoading}
          endIcon={<AdminDownloadSvg className="size-4 text-secondary-900" />}
          className="bg-transparent! text-[17px]! font-bold! tracking-[1px] text-secondary-900! uppercase hover:text-secondary-900! hover:underline"
        >
          {isLoading && loadingLabel ? loadingLabel : label}
        </Button>
      )}
    </div>
  );
};

DownloadCsv.displayName = 'DownloadCsv';
export { DownloadCsv };
